import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
} from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { ProjectService } from "../../../shared/services/project.service";
import { RiskConvergenceService } from "../../../shared/services/risk-convergence.service";
import { FilterService } from "../../../shared/services/filter.service";
import { NgxSpinnerService } from "ngx-spinner";
import { MatDialog } from "@angular/material/dialog";
import { DatePipe } from "@angular/common";
import { RisklogDialogComponent } from "./risklog-dialog/risklog-dialog.component";
import textConfiguration from "../../../../assets/static-text-configuration.json";
import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-risklog-heatmap",
  templateUrl: "./risklog-heatmap.component.html",
  styleUrls: ["./risklog-heatmap.component.scss"],
  providers: [DatePipe],
})
export class RisklogHeatmapComponent implements OnInit, OnChanges, OnDestroy {
  @Input() recommendedRisks: any;
  @Input() riskArrayHeatMap: any;
  @Input() selectedRisks: any[] = [];
  @Input() projectId: any;
  @Input() iframeEmbedded = false;
  @Input() targetRiskId: string | null = null;
  @Input() mitigationTab: string | null = null;
  @Input() isReadOnly = false;
  @Input() selectedCycleId: any = null;
  @Output() riskChanged = new EventEmitter<any>();
  riskMetric: any;
  user: any;
  commonText: any = (textConfiguration as any).common;
  moduleName: string = (textConfiguration as any)?.modules?.risknet;

  // Project Risk Deep Dive Table State
  projectRiskList: any = { count: 0, records: [] };
  allRecords: any[] = [];
  filteredRecords: any[] = [];
  selectedKpi: string | null = null;
  kpiMetrics = {
    total: 0,
    overdueCount: 0,
    overduePct: 0,
    submittedCount: 0,
    submittedPct: 0,
    underReviewCount: 0,
    underReviewPct: 0,
    wipCount: 0,
    wipPct: 0,
    closedCount: 0,
    closedPct: 0,
    closedCroCount: 0,
    closedCroPct: 0,
  };
  groupedDomains: any[] = [];
  tableLoading = false;
  sortRecords: any = 10;
  CurrentPage: any = 1;
  startValue: any = 0;
  endValue: any = 10;
  sortOptions: any[] = [10, 20, 50, 100];
  filterObj: any;
  riskMatrixConfig: any[] = [];
  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    public fb: FormBuilder,
    private readonly projectService: ProjectService,
    private readonly riskConvergenceService: RiskConvergenceService,
    private readonly filterService: FilterService,
    private readonly spinner: NgxSpinnerService,
    private readonly dialog: MatDialog,
    private readonly toastrService: ToastrService,
    public datepipe: DatePipe
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["projectId"] || changes["selectedCycleId"]) {
      this.startValue = 0;
      this.CurrentPage = 1;
      this.selectedKpi = null;
      this.fetchProjectRisks();
    }
    if (
      (changes["riskArrayHeatMap"] && this.riskArrayHeatMap?.length) ||
      (changes["recommendedRisks"] && this.recommendedRisks?.length)
    ) {
      this.mergeRecommendedRisks();
      this.groupByDomainSubdomainAndRisk();
    }
    if (changes["riskArrayHeatMap"] || changes["selectedRisks"]) {
      this.enrichRiskNodes();
    }
    if (this.targetRiskId && this.riskArrayHeatMap?.length) {
      this.openTargetRiskPopup();
    }
  }

  ngOnInit(): void {
    this.user = JSON.parse(localStorage.getItem("permission") ?? "{}");
    this.loadRiskMatrixConfig();

    this.filterService.filterValues
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: any) => {
        this.filterObj = res;
        this.startValue = 0;
        this.CurrentPage = 1;
        this.fetchProjectRisks();
      });

    if (this.recommendedRisks?.length) {
      this.mergeRecommendedRisks();
    }
    this.enrichRiskNodes();
    if (this.targetRiskId && this.riskArrayHeatMap?.length) {
      this.openTargetRiskPopup();
    }
    this.fetchProjectRisks();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  openTargetRiskPopup() {
    if (!this.targetRiskId || !this.riskArrayHeatMap) return;

    for (const domain of this.riskArrayHeatMap) {
      const subdomains = domain.children ?? domain.sub_domains ?? [];
      for (const sub of subdomains) {
        const risks = sub.children ?? sub.risks ?? [];
        for (const risk of risks) {
          if (risk.uuid === this.targetRiskId || risk.id == this.targetRiskId) {
            this.onClickRisk(domain, sub, risk, null);
            this.targetRiskId = null;
            return;
          }
        }
      }
    }
  }

  /** Stamps risk_impact and residual_risk_score from selectedRisks onto each risk node. */
  private enrichRiskNodes() {
    if (!this.riskArrayHeatMap?.length || !this.selectedRisks?.length) return;
    this.riskArrayHeatMap.forEach((domain: any) => {
      const subdomains = domain.children ?? domain.sub_domains ?? [];
      subdomains.forEach((sub: any) => {
        const risks = sub.children ?? sub.risks ?? [];
        risks.forEach((risk: any) => {
          const match = this.selectedRisks.find(
            (s: any) => s.risk_id === risk.uuid
          );
          if (match) {
            risk.risk_type = match.risk_type;
            risk.risk_impact = match.risk_impact;
            risk.residual_risk_score = match.residual_risk_score ?? null;
            risk.inherent_risk_score = match.inherent_risk_score ?? null;
            risk.status = match.status;
            risk.risk_response = match.risk_response;
            risk.color = match.color ?? risk.color;
            risk.has_ai_summary = match.has_ai_summary ?? risk.has_ai_summary ?? false;
          }
        });
      });
    });
  }

  getFilteredArray(array: any) {
    if (array !== undefined) {
      return array.filter((e: any) => e.selected);
    } else {
      return [];
    }
  }

  getBackgroundColor(color: string) {
    if (color === "green") {
      return "linear-gradient(0deg, #16a34a, #22c55e)";
    } else if (color === "teal") {
      return "#0bb797";
    } else if (color === "orange") {
      return "linear-gradient(45deg, #d97706, #f59e0b)";
    } else if (color === "amber") {
      return "linear-gradient(45deg, #f59e0b, #fbbf24)";
    } else if (color === "rose") {
      return "linear-gradient(45deg, #e11d48, #fb7185)";
    } else if (color === "red") {
      return "linear-gradient(45deg, #dc2626, #ef4444)";
    } else {
      return "grey";
    }
  }

  getTrucatedName(
    subdomain: string,
    shortDescription: string,
    riskName: string
  ) {
    if (!shortDescription) {
      shortDescription = riskName;
    }
    if (shortDescription.length > subdomain.length) {
      let subStrThreshold = 10;
      if (subdomain.length - 1 > 10) {
        subStrThreshold = subdomain.length - 1;
      }
      return shortDescription.substring(0, subStrThreshold) + "...";
    } else {
      return shortDescription;
    }
  }
  mergeRecommendedRisks() {
    this.recommendedRisks.forEach((domainBlock: any) => {
      domainBlock.subdomainList.forEach((sdBlock: any) => {
        const subdomain = sdBlock.subdomain;

        sdBlock.risks.forEach((recRisk: any) => {
          let hmDomain = this.riskArrayHeatMap.find(
            (d: any) => d.uuid === domainBlock.domain.uuid
          );
          if (!hmDomain) return;

          hmDomain.selected = true;

          let hmSubdomain = hmDomain.children.find(
            (sd: any) => sd.uuid === subdomain.uuid
          );

          if (!hmSubdomain) {
            hmSubdomain = {
              ...subdomain,
              selected: true,
              children: [],
            };
            hmDomain.children.push(hmSubdomain);
          }

          hmSubdomain.selected = true;

          let hmRisk = hmSubdomain.children.find(
            (r: any) => r.uuid === recRisk.uuid
          );

          const isMissing = !hmRisk;

          if (isMissing) {
            hmRisk = {
              ...recRisk,
              selected: true,
              color: "grey",
              is_recommended_only: true,
              status: "",
            };
            hmSubdomain.children.push(hmRisk);
          }

          if (hmRisk.project_risk_id) {
            hmRisk.is_recommended_only = false;
          } else {
            hmRisk.color = "grey";
            hmRisk.is_recommended_only = true;
          }

          hmRisk.selected = true;
        });
      });
    });
    this.recalculateColors();
    this.groupByDomainSubdomainAndRisk();
  }

  recalculateColors() {
    this.riskArrayHeatMap.forEach((domain: any) => {
      const subdomains = domain.children ?? domain.sub_domains ?? [];

      subdomains.forEach((sub: any) => {
        const risks = sub.children ?? sub.risks ?? [];

        const riskColors = risks.map((r: any) => r.color);

        if (riskColors.length && riskColors.every((c: any) => c === "grey")) {
          sub.color = "grey";
        } else {
          if (riskColors.includes("red")) sub.color = "red";
          else if (riskColors.includes("amber")) sub.color = "amber";
          else if (riskColors.includes("green")) sub.color = "green";
          else sub.color = "grey";
        }
      });

      const subColors = subdomains.map((s: any) => s.color);

      if (subColors.length && subColors.every((c: any) => c === "grey")) {
        domain.color = "grey";
      } else {
        if (subColors.includes("red")) domain.color = "red";
        else if (subColors.includes("amber")) domain.color = "amber";
        else if (subColors.includes("green")) domain.color = "green";
        else domain.color = "grey";
      }
    });
  }

  onClickRisk(domain: any, sub: any, risk: any, modal: any) {
    if (risk.is_recommended_only) {
      return;
    }
    if (this.user?.is_vendor || this.iframeEmbedded) {
      return;
    }
    this.riskMetric = {
      domain: domain.name,
      subdomain: sub.name,
      risk: risk.name,
      risk_id: risk.uuid,
      risk_response: "Mitigate",
      id: risk.id,
    };
    this.projectService
      .getRiskProjectMapped(this.projectId, this.riskMetric.risk_id, this.moduleName, this.selectedCycleId)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.riskMetric.risk_response = res.risk_response || "Mitigate";
          }
          const projectDetails: any = JSON.parse(
            localStorage.getItem("program") ?? "{}"
          );
          const selectedProjectName = JSON.parse(
            localStorage.getItem("selectedProject") ?? "{}"
          );
          const dlg = this.dialog.open(RisklogDialogComponent, {
            width: "80vw",
            height: "90vh",
            maxHeight: "100vh",
            disableClose: true,
            autoFocus: false,
            data: {
              riskMetric: this.riskMetric,
              res,
              projectId: this.projectId,
              mitigationTab: this.mitigationTab,
              isReadOnly: this.isReadOnly,
              breadCrumbData: [
                projectDetails.portfolio_name,
                projectDetails.subportfolio_name,
                projectDetails.program_name,
                selectedProjectName.project_name,
              ],
            },
          });
          dlg.afterClosed().subscribe((data: any) => {
            if (data) {
              this.updateRiskStatus(data.riskId, data.res);
              this.riskChanged.emit({
                riskId: data.riskId,
                riskResponse: data.res.risk_response,
              });
              this.fetchProjectRisks();
            }
          });
        },
        error: (err: any) => {
          this.toastrService.error(err?.error);
        },
      });
  }

  fetchProjectRisks() {
    if (!this.projectId) {
      return;
    }
    this.tableLoading = true;
    let queryParam = `&project=${this.projectId}`;
    if (this.selectedCycleId) {
      queryParam += `&cycle=${this.selectedCycleId}`;
    }
    queryParam += this.getFilterValues();

    this.riskConvergenceService
      .getFilteredRiskTracker(null, null, queryParam, this.moduleName)
      .subscribe({
        next: (res: any) => {
          this.projectRiskList = res || { count: 0, records: [] };
          this.allRecords = res?.records || [];
          this.calculateKpiMetrics();
          this.applyKpiFilterAndPaginate(false);
          this.tableLoading = false;
        },
        error: () => {
          this.tableLoading = false;
        },
      });
  }

  getFilterValues(): string {
    let filterString = "";
    const fieldMapping: any = {
      risk_domain: "risk_domain",
      risk_subdomain: "risk_sub_domain",
      risk: "risk",
      risk_response: "risk_response",
      risk_status: "status",
      impact: "impact",
      risk_type: "risk_type",
      inherent_risk_score: "inherent_risk_score",
      residual_risk_score: "residual_risk_score",
      action_ageing: "action_ageing",
      domain: "risk_domain",
      subdomain: "risk_sub_domain",
      riskResponse: "risk_response",
      riskStatus: "status",
    };

    const inputs: any[] = this.filterObj?.inputs ?? [];
    inputs.forEach((input: any) => {
      const field = fieldMapping[input.field] || input.field;
      const value = input.value;
      if (value === null || value === undefined || value === "") return;

      if (Array.isArray(value)) {
        const ids = value
          .map((v: any) => (v && typeof v === "object" ? v.uuid ?? v.id ?? v.value ?? v : v))
          .filter((v: any) => v !== null && v !== undefined && v !== "");
        if (ids.length) filterString += `&${field}=${ids.join(",")}`;
      } else if (typeof value === "object") {
        const id = value.uuid ?? value.id ?? value.value;
        if (id !== null && id !== undefined && id !== "") {
          filterString += `&${field}=${id}`;
        }
      } else {
        filterString += `&${field}=${value}`;
      }
    });

    return filterString;
  }

  calculateKpiMetrics() {
    let overdue = 0, submitted = 0, underReview = 0, wip = 0, closed = 0, closedCro = 0;
    this.allRecords.forEach((record: any) => {
      const cat = this.getRecordCategory(null, record);
      if (cat === "overdue") overdue++;
      else if (cat === "submitted") submitted++;
      else if (cat === "under_review") underReview++;
      else if (cat === "wip") wip++;
      else if (cat === "closed") closed++;
      else if (cat === "closed_cro") closedCro++;
      else submitted++;
    });
    const total = this.allRecords.length;
    this.kpiMetrics = {
      total,
      overdueCount: overdue,
      overduePct: total ? Math.round((overdue / total) * 100) : 0,
      submittedCount: submitted,
      submittedPct: total ? Math.round((submitted / total) * 100) : 0,
      underReviewCount: underReview,
      underReviewPct: total ? Math.round((underReview / total) * 100) : 0,
      wipCount: wip,
      wipPct: total ? Math.round((wip / total) * 100) : 0,
      closedCount: closed,
      closedPct: total ? Math.round((closed / total) * 100) : 0,
      closedCroCount: closedCro,
      closedCroPct: total ? Math.round((closedCro / total) * 100) : 0,
    };
  }

  isKpiSelected(kpiKey: string): boolean {
    if (kpiKey === "all") {
      return !this.selectedKpi || this.selectedKpi === "all";
    }
    return this.selectedKpi === kpiKey;
  }

  toggleKpiFilter(kpiKey: string) {
    if (kpiKey === "all" || this.selectedKpi === kpiKey) {
      this.selectedKpi = null;
    } else {
      this.selectedKpi = kpiKey;
    }
    this.applyKpiFilterAndPaginate(true);
  }

  applyKpiFilterAndPaginate(resetPage: boolean = true) {
    if (resetPage) {
      this.CurrentPage = 1;
    }

    if (!this.selectedKpi || this.selectedKpi === "all") {
      this.filteredRecords = [...this.allRecords];
    } else {
      this.filteredRecords = this.allRecords.filter((r: any) => this.getRecordCategory(null, r) === this.selectedKpi);
    }

    const pageSize = parseInt(this.sortRecords, 10) || 10;
    const pageIndex = Math.max(0, (parseInt(this.CurrentPage, 10) || 1) - 1);
    this.startValue = pageIndex * pageSize;
    this.endValue = Math.min(this.startValue + pageSize, this.filteredRecords.length);

    const pageSlice = this.filteredRecords.slice(this.startValue, this.endValue);
    this.groupByDomainSubdomainAndRisk(pageSlice);
  }

  groupByDomainSubdomainAndRisk(recordsList?: any[]) {
    const records = recordsList || (this.filteredRecords?.length ? this.filteredRecords.slice(this.startValue, this.endValue) : (this.projectRiskList?.records || []));

    const grouped: any[] = records.reduce((acc: any, row: any) => {
      const domainKey = row.risk_domain || "Others";
      const subDomainKey = row.risk_sub_domain || "Others";
      const riskKey = row.risk || "Others";

      // DOMAIN
      let domainGroup = acc.find((d: any) => d.domainName === domainKey);
      if (!domainGroup) {
        domainGroup = { domainName: domainKey, totalRows: 0, subDomains: [] };
        acc.push(domainGroup);
      }

      // SUBDOMAIN
      let subDomainGroup = domainGroup.subDomains.find(
        (s: any) => s.subDomainName === subDomainKey
      );
      if (!subDomainGroup) {
        subDomainGroup = {
          subDomainName: subDomainKey,
          totalRows: 0,
          risks: [],
        };
        domainGroup.subDomains.push(subDomainGroup);
      }

      // RISK
      let riskGroup = subDomainGroup.risks.find(
        (r: any) => r.riskName === riskKey
      );
      if (!riskGroup) {
        riskGroup = {
          riskName: riskKey,
          riskId: row.risk_id,
          totalRows: 0,
          records: [],
        };
        subDomainGroup.risks.push(riskGroup);
      }

      riskGroup.records.push(row);

      riskGroup.totalRows += 1;
      subDomainGroup.totalRows += 1;
      domainGroup.totalRows += 1;

      return acc;
    }, []);

    this.groupedDomains = grouped;
  }

  getRecords() {
    this.applyKpiFilterAndPaginate(false);
  }

  onPageSizeChange() {
    this.CurrentPage = 1;
    this.applyKpiFilterAndPaginate(true);
  }

  hasAiSummary(risk: any, record?: any): boolean {
    if (risk?.has_ai_summary) return true;
    if (record?.has_ai_summary) return true;
    const recordId = record?.id;
    const riskId = record?.risk_id || record?.risk_uuid || risk?.riskId || risk?.uuid || risk?.id;
    const riskName = (record?.risk || risk?.riskName || "").trim().toLowerCase();

    if (this.selectedRisks?.length) {
      const match = this.selectedRisks.find(
        (s: any) =>
          (recordId != null && (s.id == recordId || s.project_risk_id == recordId)) ||
          (riskId != null && (s.risk_id == riskId || s.id == riskId)) ||
          (riskName && s.risk_name && s.risk_name.trim().toLowerCase() === riskName)
      );
      if (match && match.has_ai_summary) {
        return true;
      }
    }

    if (this.riskArrayHeatMap?.length) {
      for (const d of this.riskArrayHeatMap) {
        for (const sd of (d.children || d.sub_domains || [])) {
          for (const r of (sd.children || sd.risks || [])) {
            const rName = (r.name || r.risk_name || "").trim().toLowerCase();
            if (
              (riskId != null && (r.uuid == riskId || r.id == riskId)) ||
              (riskName && rName && rName === riskName)
            ) {
              if (r.has_ai_summary) return true;
            }
          }
        }
      }
    }

    return false;
  }

  isRiskSavedInCycle(record: any): boolean {
    if (!record) return false;
    const response = (record.risk_response || "").toLowerCase();
    if (response === "mitigate" || response === "accept") {
      return true;
    }
    if (record.residual_risk_score && Number(record.residual_risk_score) > 0) {
      return true;
    }
    return false;
  }

  getInherentScore(record: any): any {
    const raw = record?.inherent_risk_score;
    if (raw === null || raw === undefined || raw === "" || raw === "-" || isNaN(Number(raw))) {
      return 0;
    }
    return Number(raw);
  }

  getResidualScore(record: any): any {
    const raw = record?.residual_risk_score;
    if (raw === null || raw === undefined || raw === "" || raw === "-" || isNaN(Number(raw))) {
      return 0;
    }
    return Number(raw);
  }

  loadRiskMatrixConfig() {
    this.riskConvergenceService
      .getRiskMatrixConfig()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((config: any) => {
        if (Array.isArray(config) && config.length) {
          this.riskMatrixConfig = config;
        }
      });
  }

  getRiskScoreColor(score: any): string {
    return this.riskConvergenceService.getRiskScoreColorFromConfig(score, this.riskMatrixConfig);
  }

  getScoreTooltip(score: any): string {
    return this.riskConvergenceService.getScoreTooltip(score, this.riskMatrixConfig);
  }

  isClosedByCro(risk?: any, record?: any): boolean {
    if (!record && risk) {
      record = risk;
    }
    const checkStatus = (val: any): boolean => {
      if (!val) return false;
      if (typeof val === "string") {
        const lower = val.toLowerCase().trim();
        return lower === "closed by cro";
      }
      const statusVal = val.status_label ?? val.status ?? val.risk_status ?? val.riskStatus;
      if (!statusVal) return false;
      const str = (typeof statusVal === "string" ? statusVal : (statusVal.name || statusVal.display_name || "")).toLowerCase().trim();
      return str === "closed by cro";
    };

    if (checkStatus(record) || checkStatus(risk)) {
      return true;
    }

    const recordId = record?.id;
    const riskId = record?.risk_id || record?.risk_uuid || risk?.riskId || risk?.uuid || risk?.id;
    const riskName = (record?.risk || risk?.riskName || "").trim().toLowerCase();

    if (this.selectedRisks?.length) {
      const match = this.selectedRisks.find(
        (s: any) =>
          (recordId != null && (s.id == recordId || s.project_risk_id == recordId)) ||
          (riskId != null && (s.risk_id == riskId || s.id == riskId)) ||
          (riskName && s.risk_name && s.risk_name.trim().toLowerCase() === riskName)
      );
      if (match && checkStatus(match.status)) {
        return true;
      }
    }

    if (risk?.records?.length) {
      if (risk.records.some((r: any) => checkStatus(r))) {
        return true;
      }
    }

    return false;
  }

  getRecordCategory(risk?: any, record?: any): string {
    if (!record && risk) {
      record = risk;
    }
    if (!record) return "submitted";

    if (record.records && Array.isArray(record.records)) {
      if (record.records.length === 0) return "submitted";
      const cats = record.records.map((r: any) => this.getRecordCategory(r));
      if (cats.includes("overdue")) return "overdue";
      if (cats.includes("under_review")) return "under_review";
      if (cats.includes("wip")) return "wip";
      if (cats.includes("submitted")) return "submitted";
      if (cats.includes("closed")) return "closed";
      if (cats.includes("closed_cro")) return "closed_cro";
      return cats[0] || "submitted";
    }

    // 1. Closed by CRO
    if (this.isClosedByCro(risk, record)) {
      return "closed_cro";
    }

    // Extract status string
    const statusVal = record.status_label ?? record.status ?? record.risk_status ?? record.riskStatus;
    let statusStr = "";
    if (typeof statusVal === "string") {
      statusStr = statusVal.toLowerCase().trim();
    } else if (statusVal && typeof statusVal === "object") {
      statusStr = (statusVal.display_name || statusVal.name || statusVal.label || "").toLowerCase().trim();
    }

    // 2. Closed by project
    if (statusStr === "completed" || statusStr === "closed") {
      return "closed";
    }

    // 3. Overdue (unclosed risks past due date or action plan date)
    if (this.isRecordOverdue(record)) {
      return "overdue";
    }

    // 4. Under Review
    if (statusStr.includes("under review") || statusStr.includes("review")) {
      return "under_review";
    }

    // 5. WIP (Work in progress)
    if (
      statusStr.includes("wip") ||
      statusStr.includes("in progress") ||
      statusStr.includes("progress")
    ) {
      return "wip";
    }

    // 6. Submitted (or Open / initial state without response)
    if (statusStr.includes("submitted") || statusStr.includes("open")) {
      return "submitted";
    }

    const response = (record.risk_response || "").toLowerCase().trim();
    if (!response || response === "-" || response === "none") {
      return "submitted";
    }

    // Default fallback
    return "wip";
  }

  isRecordOverdue(record: any): boolean {
    if (!record) return false;
    const statusVal = record.status_label ?? record.status ?? record.risk_status ?? record.riskStatus;
    let statusStr = "";
    if (typeof statusVal === "string") {
      statusStr = statusVal.toLowerCase().trim();
    } else if (statusVal && typeof statusVal === "object") {
      statusStr = (statusVal.display_name || statusVal.name || statusVal.label || "").toLowerCase().trim();
    }
    if (statusStr.includes("closed") || statusStr === "completed") return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const details = record.details || [];
    const hasActionOverdue = details.some((d: any) => {
      const actionStatus = (d?.status || "").toLowerCase();
      if (actionStatus.includes("closed") || actionStatus.includes("complete")) return false;
      if (d?.planned_end_date) {
        const dDate = new Date(d.planned_end_date);
        return !isNaN(dDate.getTime()) && dDate < today;
      }
      return false;
    });

    const hasRiskDueOverdue = record.due_date && !isNaN(new Date(record.due_date).getTime()) && new Date(record.due_date) < today;

    return Boolean(hasActionOverdue || hasRiskDueOverdue);
  }

  getRiskColor(risk?: any, record?: any): string {
    const cat = this.getRecordCategory(risk, record);
    if (cat === "overdue") return "red";
    if (cat === "submitted") return "rose";
    if (cat === "under_review") return "amber";
    if (cat === "wip") return "orange";
    if (cat === "closed") return "green";
    if (cat === "closed_cro") return "teal";
    return "grey";
  }

  getRiskColorLabel(color: string): string {
    if (color === "red") return "Overdue";
    if (color === "rose") return "Submitted";
    if (color === "amber") return "Under Review";
    if (color === "orange") return "WIP";
    if (color === "green") return "Closed";
    if (color === "teal") return "Closed";
    return "";
  }

  getScoreBadgeClass(score: any): string {
    const num = Number(score);
    if (isNaN(num)) return "";
    if (num >= 15) return "badge-critical";
    if (num >= 10) return "badge-high";
    if (num >= 5) return "badge-medium";
    return "badge-low";
  }

  onTableRiskClick(domain: any, subDomain: any, risk: any, record: any, selectedMainTabIndex: number = 0) {
    if (this.user?.is_vendor || this.iframeEmbedded) {
      return;
    }
    if (risk?.is_recommended_only || record?.is_recommended_only) {
      return;
    }
    const domainName = domain?.domainName || domain?.name || record?.risk_domain;
    const subDomainName = subDomain?.subDomainName || subDomain?.name || record?.risk_sub_domain;
    const riskName = risk?.riskName || risk?.name || record?.risk;
    const riskId = record?.risk_id || record?.risk_uuid || record?.id;

    this.riskMetric = {
      domain: domainName,
      subdomain: subDomainName,
      risk: riskName,
      risk_id: riskId,
      risk_response: record?.risk_response || "Mitigate",
      id: record?.id,
    };

    this.spinner.show();
    this.projectService
      .getRiskProjectMapped(this.projectId, this.riskMetric.risk_id, this.moduleName, this.selectedCycleId)
      .subscribe({
        next: (res: any) => {
          this.spinner.hide();
          if (res) {
            this.riskMetric.risk_response = res.risk_response || "Mitigate";
            this.riskMetric.id = res.id || this.riskMetric.id || record?.id;
          }
          const projectDetails: any = JSON.parse(
            localStorage.getItem("program") ?? "{}"
          );
          const selectedProjectName = JSON.parse(
            localStorage.getItem("selectedProject") ?? "{}"
          );
          const dlg = this.dialog.open(RisklogDialogComponent, {
            width: "80vw",
            height: "90vh",
            maxHeight: "100vh",
            disableClose: true,
            autoFocus: false,
            data: {
              riskMetric: this.riskMetric,
              res,
              projectId: this.projectId,
              mitigationTab: this.mitigationTab,
              selectedMainTabIndex,
              isReadOnly: this.isReadOnly,
              breadCrumbData: [
                projectDetails.portfolio_name,
                projectDetails.subportfolio_name,
                projectDetails.program_name,
                selectedProjectName.project_name,
              ],
            },
          });
          dlg.afterClosed().subscribe((data: any) => {
            if (data) {
              this.updateRiskStatus(data.riskId, data.res);
              this.riskChanged.emit({
                riskId: data.riskId,
                riskResponse: data.res.risk_response,
              });
              this.fetchProjectRisks();
            }
          });
        },
        error: (err: any) => {
          this.spinner.hide();
          this.toastrService.error(err?.error || "Failed to load risk details");
        },
      });
  }

  updateRiskStatus(riskId: any, res: any) {
    if (!this.riskArrayHeatMap) return;
    let statusUpdated = false;
    for (const domain of this.riskArrayHeatMap) {
      for (const subdomain of domain.sub_domains || domain.children || []) {
        const risk = (subdomain.risks || subdomain.children || []).find(
          (item: any) => item.uuid === riskId || item.id === riskId
        );
        if (risk) {
          risk.status = res.risk_response;
          statusUpdated = true;
          break;
        }
      }
      if (statusUpdated) {
        break;
      }
    }
  }

  hasAnyVisibleRisks(): boolean {
    return this.getFilteredArray(this.riskArrayHeatMap).some((d: any) =>
      this.getFilteredArray(d.children).some(
        (sd: any) => this.getFilteredArray(sd.children).length > 0
      )
    );
  }

  closeDialog() {
    this.dialog.closeAll();
  }
}
